import { dictOf, SOURCE_LANG } from './i18n';
import { LIB } from './library';
import type { Project } from './types';

/*
 * Translation of project texts (EPLAN "Translation"): page titles, device
 * descriptions and free texts are replaced through a company dictionary
 * (source text → text in the target language). Device tags, part numbers and
 * technical values are never touched. Texts the application generated itself
 * ("M1 motor koruma") are recognised through the interface translations.
 */

export interface ProjectText {
  text: string;
  /** how many times it appears */
  count: number;
  /** where, for the dialog: "page" | "device" | "text" | "project" */
  kinds: string[];
}

/** Every translatable text of a project, most frequent first. */
export function projectTexts(pr: Project): ProjectText[] {
  const m = new Map<string, ProjectText>();
  const add = (v: string | undefined, kind: string) => {
    const text = (v ?? '').trim();
    if (!text || !/\p{L}{2,}/u.test(text)) return;
    const e = m.get(text) ?? m.set(text, { text, count: 0, kinds: [] }).get(text)!;
    e.count++;
    if (!e.kinds.includes(kind)) e.kinds.push(kind);
  };
  add(pr.name, 'project');
  add(pr.customer, 'project');
  for (const p of pr.pages) {
    add(p.desc, 'page');
    for (const s of p.syms) {
      if (LIB[s.type]?.role === 'text') add(s.props.text, 'text');
      else add(s.desc, 'device');
    }
  }
  return [...m.values()].sort((a, b) => b.count - a.count || a.text.localeCompare(b.text, 'tr'));
}

const escRe = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const patterns = new Map<string, { re: RegExp; to: string }[]>();

/** Suggestion from the interface translations: exact text or a pattern such as "{0} motor koruma". */
export function suggestTranslation(text: string, lang: string): string | undefined {
  if (lang === SOURCE_LANG) return undefined;
  const d = dictOf(lang);
  if (d[text]) return d[text];
  let list = patterns.get(lang);
  if (!list) {
    list = Object.entries(d)
      .filter(([k]) => /\{\d\}/.test(k) && !/[<>]/.test(k) && k.replace(/\{\d\}/g, '').trim().length >= 3)
      .map(([k, to]) => ({ re: new RegExp('^' + escRe(k).replace(/\\\{(\d)\\\}/g, '(?<p$1>.+?)') + '$'), to }));
    patterns.set(lang, list);
  }
  for (const { re, to } of list) {
    const m = re.exec(text);
    if (m?.groups) return to.replace(/\{(\d)\}/g, (_, i) => {
      const v = m.groups![`p${i}`] ?? '';
      return d[v] ?? v;
    });
  }
  return undefined;
}

/** A copy of the project with its texts replaced through the dictionary (and suggestions when asked). */
export function translateProject(pr: Project, dict: Record<string, string>, lang?: string): Project {
  const out = JSON.parse(JSON.stringify(pr)) as Project;
  const tr = (v: string | undefined) => {
    const k = (v ?? '').trim();
    if (!k) return v ?? '';
    return dict[k] || (lang ? suggestTranslation(k, lang) : undefined) || v || '';
  };
  out.name = tr(out.name);
  out.customer = tr(out.customer);
  for (const p of out.pages) {
    p.desc = tr(p.desc);
    for (const s of p.syms) {
      if (LIB[s.type]?.role === 'text') s.props = { ...s.props, text: tr(s.props.text) };
      else s.desc = tr(s.desc);
    }
  }
  return out;
}
